/**
 * Pure helpers for the Grade Center summary band (KPIs, distribution, leaderboard).
 * Kept separate from score.js so presentation can evolve without touching fetch logic.
 */

/** Two-letter initials for avatar chips. */
export function toInitials(name) {
  return (
    String(name || '?')
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

/**
 * Build display-only KPI / distribution / leaderboard data from grouped grade rows.
 * Returns null when there are no scored assessments to summarize.
 *
 * @param {Array<{ firstName?: string, lastName?: string, courseName?: string, assessments?: Array<{ percentage?: number }> }>} filteredData
 */
export function buildGradeSummary(filteredData) {
  const all = [];
  const students = (filteredData || [])
    .map((s) => {
      const ps = (s.assessments || [])
        .map((a) => a.percentage)
        .filter((n) => typeof n === 'number' && !Number.isNaN(n));
      ps.forEach((p) => all.push(p));
      const avg = ps.length ? ps.reduce((x, y) => x + y, 0) / ps.length : 0;
      return {
        name: `${s.firstName || ''} ${s.lastName || ''}`.trim() || '—',
        courseName: s.courseName || '—',
        avg,
        count: ps.length,
      };
    })
    .filter((s) => s.count > 0);

  if (all.length === 0) return null;

  const avg = Math.round(all.reduce((a, b) => a + b, 0) / all.length);
  const pass = all.filter((p) => p >= 70).length;
  const passRate = Math.round((pass / all.length) * 100);
  const top = Math.round(Math.max(...all));
  const bands = [
    { band: '90–100', key: 'success', test: (x) => x >= 90 },
    { band: '80–89', key: 'accent', test: (x) => x >= 80 && x < 90 },
    { band: '70–79', key: 'warning', test: (x) => x >= 70 && x < 80 },
    { band: '<70', key: 'danger', test: (x) => x < 70 },
  ];
  const dist = bands.map((b) => ({ ...b, n: all.filter(b.test).length }));
  const maxN = Math.max(...dist.map((d) => d.n), 1);
  const leaderboard = [...students]
    .sort((a, b) => b.avg - a.avg)
    .slice(0, 6);

  return { total: all.length, avg, passRate, top, dist, maxN, leaderboard };
}
